import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel } from "../../lib/format";
import { KPI, Card, Loading } from "../../components/UI";
import { Home, Users, FileSignature, Wallet, Wrench, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

export default function ManagerDashboard() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [leases, setLeases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/maintenance"),
      api.get("/leases"),
      api.get("/payments"),
    ]).then(([s, t, l, p]) => {
      setStats(s.data); setTickets(t.data); setLeases(l.data); setPayments(p.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <Loading/>;
  const pendingSig = leases.filter(l => ["sent_to_tenant","tenant_signed","manager_signed"].includes(l.lease_status));
  const pendingPay = payments.filter(p => p.payment_status === "pending");
  const openTickets = tickets.filter(t => ["open","assigned","in_progress"].includes(t.status));

  const exportExpiringCSV = async () => {
    try {
      const res = await api.get("/reports/leases-expiring.csv?days=60", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leases-expiring-60d-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Expiring leases CSV downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A227]">PROPERTY MANAGER</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24]">Welcome, Arch. Keshinro</h1>
        <p className="text-[#5E7166] mt-1 text-sm">Day-to-day operations of Modav Homes.</p>
      </div>

      {stats.anomalies.length > 0 && (
        <div className="card-modav p-4 border-l-4 border-[#C9A227] bg-[#FDF6E3]/30">
          <div className="flex items-start gap-2"><AlertTriangle className="text-[#A0801C] mt-0.5" size={18}/>
            <div className="text-sm text-[#1A2E24]"><b>{stats.anomalies.length} data alerts</b> — review on Admin dashboard.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Vacant Units" value={stats.vacant} icon={Home} testid="kpi-vacant"/>
        <KPI label="Open Tickets" value={openTickets.length} icon={Wrench} testid="kpi-open-tickets"/>
        <KPI label="Pending Signatures" value={pendingSig.length} icon={FileSignature} testid="kpi-pending-sig"/>
        <KPI label="Pending Payments" value={pendingPay.length} icon={Wallet} testid="kpi-pending-pay"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Vacant Units — Onboard Tenants" action={<Link to="/units" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">All units →</Link>}>
          {Object.entries(stats.by_unit_type).filter(([,v]) => v.vacant > 0).map(([k,v]) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-[#E5EBE8] last:border-0 text-sm">
              <div>{k}</div>
              <span className="badge badge-red">{v.vacant} vacant</span>
            </div>
          ))}
        </Card>
        <Card title="Leases Pending Signature">
          {pendingSig.length === 0 ? <div className="text-sm text-[#5E7166] py-4">All caught up!</div> :
            pendingSig.slice(0,6).map(l => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-[#E5EBE8] last:border-0 text-sm">
                <div>
                  <div className="font-semibold">{l.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-[#5E7166]">{l.unit?.unit_name} • {naira(l.total_payable)}</div>
                </div>
                <Link to={`/leases/${l.id}`} className="btn-outline-emerald text-xs">Review</Link>
              </div>
            ))}
        </Card>
      </div>

      <Card title="Open Maintenance Tickets" action={<Link to="/maintenance" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">All tickets →</Link>}>
        {openTickets.length === 0 ? <div className="text-sm text-[#5E7166] py-4">No open tickets.</div> :
          <ul className="divide-y divide-[#E5EBE8]">
            {openTickets.slice(0,6).map(t => (
              <li key={t.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold">{t.title}</div>
                  <div className="text-xs text-[#5E7166]">{t.tenant?.full_name} • {t.unit?.unit_name} • {formatDate(t.created_at)}</div>
                </div>
                <span className={statusBadgeClass(t.status)}>{statusLabel(t.status)}</span>
              </li>
            ))}
          </ul>}
      </Card>

      {stats.lease_expiring && stats.lease_expiring.length > 0 && (
        <Card
          title={`Leases Expiring Soon (≤ 60 days) — ${stats.lease_expiring.length}`}
          testid="lease-expiring"
          action={
            <button
              onClick={exportExpiringCSV}
              className="btn-outline-emerald text-xs inline-flex items-center gap-1.5"
              data-testid="export-expiring-csv"
              title="Download CSV of all leases expiring in the next 60 days"
            >
              <Download size={14}/> Export CSV
            </button>
          }
        >
          <ul className="divide-y divide-[#E5EBE8]">
            {stats.lease_expiring.map(l => {
              const urgency = l.days_left <= 7 ? "text-[#C92A2A]" : l.days_left <= 30 ? "text-[#A0801C]" : "text-[#5E7166]";
              return (
                <li key={l.id} className="py-3 flex items-center justify-between text-sm gap-3" data-testid={`expiring-row-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{l.tenant_name || "—"}</div>
                    <div className="text-xs text-[#5E7166] truncate">{l.unit_name || "—"} • Ends {formatDate(l.lease_end_date)}</div>
                  </div>
                  <div className={`text-xs font-semibold whitespace-nowrap ${urgency}`}>
                    {l.days_left === 0 ? "Today" : `${l.days_left}d left`}
                  </div>
                  <Link to={`/leases/${l.id}`} className="btn-outline-emerald text-xs">Review</Link>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
